import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';
export interface TicketSearch {
  id: number;
  flag: string;
  userName: string;
  priority: string;
  summary: string;
  oldMotorNumber: number;
  serialNumber: string;
  machineName: string;
  sparePartNumber: string;
  childTicket: string;
  projectNumber: string | null;
  createdDate: string;
  createdDateCETFormatted: string;
  targetDate: string;
  targetDateCETFormatted: string;
  ticketStatus: string;
  ticketStatusId: number;
  motorInvestigationFlowId?: number;
  lastSavedPage?: string;
  isInvestigationInternal?: boolean;
  ticketSubject?: string;
  ticketType?: string;
  ticketExtension?: string;
}

export interface MotorInvestigationTicketResponse {
  totalResult: number;
  newTicketCount: number;
  assignedTicketCount: number;
  inProgressTicketCount: number;
  submittedTicketCount: number;
  followUpTicketCount: number;
  solutionLiveTicketCount: number;
  motorInvestigationTickets: TicketSearch[];
}

export interface TicketSearchResponse {
  totalResult: number;
  newTicketCount: number;
  assignedTicketCount: number;
  inProgressTicketCount: number;
  submittedTicketCount: number;
  followUpTicketCount: number;
  solutionLiveTicketCount: number;
  motorInvestigationTickets: TicketSearch[];
}

export interface MotorInvestigationTicketInsights {
  newTicketsCount: number;
  onGoingTicketsCount: number;
  inApprovalCount: number;
  accomplishedWeekCount: number;
  accomplishedFortnightCount: number;
  accomplishedMonthCount: number;
  accomplishedYearCount: number;
}

export interface FetchTicketSearchParams {
  page?: number;
  limit?: number;
  sort?: keyof TicketSearch;
  order?: 'asc' | 'desc';
  [key: string]: string | number | undefined;
}

const BASE_URL = process.env.REACT_APP_MOTER_SERVICE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_MOTER_SERVICE_API_BASE_URL environment variable is not set');
}

const baseQuery = createBackendBaseQuery(BASE_URL);

export const motorInvestigationTicketApiSlice = createApi({
  reducerPath: 'motorInvestigationTicketApi',
  baseQuery: baseQuery,
  tagTypes: ['Tickets','TicketDetails','TicketInsights'],
  endpoints(builders) {
    return {
      fetchTicketSearch: builders.query<
        MotorInvestigationTicketResponse,
        FetchTicketSearchParams | void
      >({
        query: (params) => {
          let queryString = '';
          if (params) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                usp.append(key, String(value));
              }
            });
            queryString = usp.toString() ? `?${usp.toString()}` : '';
          }
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION.SEARCH}${queryString}`,
            method: 'GET',
          };
        },
        providesTags: ['Tickets','TicketDetails'],
        transformErrorResponse: transformApiError,
      }),
      fetchTicketDetails: builders.query<
        MotorInvestigationTicketResponse,
        number
      >({
        query: (ticketId) => {
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION.SEARCH}?id=${ticketId}`,
            method: 'GET',
          };
        },
        providesTags: ['TicketDetails'],
        transformErrorResponse: transformApiError,
      }),
      fetchMotorInvestigationTicketInsights: builders.query<MotorInvestigationTicketInsights, void>({
        query: () => {
          return {
            url: API_URLS.MOTOR_INVESTIGATION.INSIGHTS,
            method: 'GET',
          };
        },
        providesTags: ['TicketInsights'],
        transformErrorResponse: transformApiError,
      }),
      closeInvestigationStatus: builders.mutation<number, { motorInvestigationTicketId: number }>({
        query({ motorInvestigationTicketId }) {
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION.CLOSE_INVESTIGATION_STATUS}?motorInvestigationTicketId=${motorInvestigationTicketId}`,
            method: 'POST'
          };
        },
        invalidatesTags: ['Tickets', 'TicketDetails', 'TicketInsights'],
        transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const { useFetchTicketSearchQuery, useLazyFetchTicketSearchQuery, useFetchTicketDetailsQuery, useLazyFetchTicketDetailsQuery, useCloseInvestigationStatusMutation,
  useFetchMotorInvestigationTicketInsightsQuery, useLazyFetchMotorInvestigationTicketInsightsQuery } =
  motorInvestigationTicketApiSlice;
